import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useCurrentUser, useLogout } from '../hooks/useAuth'

const NAV = [
  { to: '/portal/dashboard', label: 'Dashboard', emoji: '🏠' },
  { to: '/portal/appointments', label: 'Appointments', emoji: '📅' },
  { to: '/portal/pets', label: 'Pets', emoji: '🐾' },
  { to: '/portal/invoices', label: 'Invoices', emoji: '🧾' },
] as const

export default function PortalLayout() {
  const { data: user, isLoading } = useCurrentUser()
  const logout = useLogout()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  useEffect(() => {
    if (isLoading) return
    if (!user) {
      navigate(`/portal/sign-in?return=${encodeURIComponent(pathname)}`, { replace: true })
    }
  }, [isLoading, user, navigate, pathname])

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-nala-surface">
        <div className="text-nala-mute font-fraunces text-lg italic">Loading…</div>
      </div>
    )
  }

  const initials = `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase() || 'C'

  return (
    <div className="min-h-screen flex flex-col bg-nala-surface">
      {/* Top nav */}
      <header className="bg-white border-b border-nala-line sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/portal/dashboard" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-nala-accent text-nala-accentInk flex items-center justify-center font-fraunces font-semibold text-sm">
              N
            </div>
            <span className="font-fraunces font-semibold text-nala-ink hidden sm:inline text-[15px]">
              Nala<span className="text-nala-clay italic">.vet</span>
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium text-nala-ink">{user.first_name} {user.last_name}</span>
              <span className="text-xs text-nala-mute">{user.email}</span>
            </div>
            <div className="w-8 h-8 rounded-full bg-nala-soft text-nala-accent font-fraunces font-semibold text-sm flex items-center justify-center">
              {initials}
            </div>
            <button
              onClick={() => { logout.mutate(); navigate('/portal/sign-in') }}
              className="text-xs text-nala-mute hover:text-nala-ink transition ml-1"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto w-full flex-1 flex flex-col md:flex-row gap-6 px-5 py-7">
        {/* Sidebar */}
        <aside className="md:w-52 shrink-0">
          <nav className="bg-white rounded-2xl border border-nala-line p-2 flex md:flex-col gap-1 overflow-x-auto">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/portal/dashboard'}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-nala-accent text-nala-accentInk font-semibold'
                      : 'text-nala-mute hover:bg-nala-surface hover:text-nala-ink'
                  }`
                }
              >
                <span aria-hidden className="text-base">{item.emoji}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}

            <div className="hidden md:block mt-2 pt-2 border-t border-nala-line">
              <Link
                to="/book"
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-nala-clay font-semibold hover:bg-nala-soft transition-all"
              >
                <span aria-hidden>✦</span>
                <span>Book a visit</span>
              </Link>
            </div>
          </nav>
        </aside>

        {/* Page content */}
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
